import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditIcon from "@mui/icons-material/EditOutlined";
import { IconButton, Stack, Tooltip, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid-premium";
import { useMemo } from "react";
import RequiredFieldMarker from "@entrilia/shared/components/RequiredFieldMarker";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import type { FieldSource } from "../../../../api/types/objectTypes";
import type { WorkflowActivityOutputField } from "../../../../api/types/workflowTypes";
import { fieldTypeCaptionMap } from "../../../../utilities/enumCaptions";
import FieldIcon from "../../../common/domain-icons/FieldIcon";
import FieldSourceBadge from "../../../entityFields/FieldSourceBadge";
import WorkflowTemplateDetailsContext from "./WorkflowTemplateDetailsContext";

const getFieldSource = (field: WorkflowActivityOutputField): FieldSource =>
  field.isCustomField ? "UserDefined" : "Portal";

const WorkflowActivityFieldName = ({ field }: { field: WorkflowActivityOutputField }) => (
  <Stack direction="row" spacing={1} sx={{ alignItems: "center", width: "100%", minWidth: 0 }}>
    <FieldIcon fieldType={field.type} color="action" sx={{ flexShrink: 0 }} />
    <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
      <Typography noWrap>
        {field.displayName}
        {field.attributes.includes("Required") && <RequiredFieldMarker />}
      </Typography>
      <TypographyTooltipEllipsis
        text={field.description}
        typographyProps={{ variant: "body2", color: "textSecondary" }}
      />
    </Stack>
  </Stack>
);

interface Actions {
  hasEditPermissions: boolean;
  onEditField: (field: WorkflowActivityOutputField) => void;
  onDeleteField: (field: WorkflowActivityOutputField) => void;
}

const getColumnDefinitions = ({
  hasEditPermissions,
  onEditField,
  onDeleteField,
}: Actions): GridColDef<WorkflowActivityOutputField>[] => [
  {
    field: "displayName",
    headerName: "Name",
    flex: 4,
    minWidth: 240,
    renderCell: ({ row }) => <WorkflowActivityFieldName field={row} />,
  },
  {
    field: "type",
    headerName: "Type",
    flex: 1,
    minWidth: 96,
    valueGetter: (_, row) => fieldTypeCaptionMap[row.type],
  },
  {
    field: "source",
    headerName: "Source",
    flex: 1,
    minWidth: 96,
    renderCell: ({ row }) => <FieldSourceBadge fieldSource={getFieldSource(row)} />,
  },
  {
    field: "actions",
    headerName: "Actions",
    width: 104,
    align: "right",
    headerAlign: "right",
    cellClassName: "grid-row-actions",
    renderCell: ({ row }) => {
      if (!hasEditPermissions) {
        return null;
      }

      return (
        <>
          <Tooltip title="Edit" arrow>
            <IconButton onClick={() => onEditField(row)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          {row.isCustomField && (
            <Tooltip title="Remove" arrow>
              <IconButton color="error" onClick={() => onDeleteField(row)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </>
      );
    },
  },
];

interface Props {
  fields: WorkflowActivityOutputField[];
  onEditField: (field: WorkflowActivityOutputField) => void;
  onDeleteField: (field: WorkflowActivityOutputField) => void;
}

const WorkflowTemplateActivityFieldsGrid = ({ fields, onEditField, onDeleteField }: Props) => {
  const { hasEditPermissions } = useDefinedContext(WorkflowTemplateDetailsContext);

  const columns = useMemo(
    () => getColumnDefinitions({ hasEditPermissions, onEditField, onDeleteField }),
    [hasEditPermissions, onEditField, onDeleteField]
  );

  return (
    <DataGrid<WorkflowActivityOutputField>
      rows={fields}
      columns={columns}
      noRowsText="No fields"
      multilineRows
      disableColumnReorder
      disableColumnSorting
    />
  );
};

export default WorkflowTemplateActivityFieldsGrid;
